/**
 * Broadcasts API client (T6).
 *
 * Mirrors `/v1/broadcasts/*` + `/v1/broadcast-runs/*` (cdp-engine T6).
 */
import { apiClient } from "./client";
import type { ChannelType } from "./channels";

export type TrafficCategory = "AUTH" | "SVC" | "ADV" | "TRX";

export type BroadcastStatus =
  | "draft"
  | "cost_estimated"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "scheduled"
  | "sending"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled";

export type BroadcastRunStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "paused";

export type MessageStatus =
  | "queued"
  | "sent"
  | "delivered"
  | "failed"
  | "opened"
  | "clicked"
  | "unsubscribed"
  | "bounced"
  | "ack"
  | "cancelled";

/**
 * Эпик AB — одна «рука» A/B-варианта контента. Форма доменного типа Variant:
 * id (A/B/…), её шаблон, человеческая метка и вес (allocation, нормализуется
 * детерминированным назначением на стороне движка).
 */
export interface BroadcastVariant {
  id: string;
  /** Контент-A/B — шаблон руки. Для A/B времени отправки не нужен (армы делят
   *  базовый шаблон рассылки) — потому опционален. */
  template_id?: string | null;
  label?: string | null;
  allocation: number;
  /** Фича 4 — слот «A/B времени отправки»: локальный час [0..23] этой армы. Когда
   *  задан у всех арм — рассылка становится экспериментом времени отправки. */
  send_hour?: number | null;
}

/**
 * Эпик Cal — окно доставки (часы × дни недели × тип дня в ЧП получателя).
 * Любая ось пустая = «любой». NULL-окно = без ограничения.
 */
export interface DeliveryWindow {
  allowed_days?: string[];     // 'MON'..'SUN'
  start_time?: string | null;  // 'HH:MM'
  end_time?: string | null;    // 'HH:MM'
  day_types?: string[];        // 'workday'|'weekend'|'holiday'|'pre_holiday'
}

/**
 * Фаза 4 — инлайн-карточка канала «Мессенджер» (на проводе snake_case, как узел
 * журнея «Отправить в мессенджер»). Реюз схемы content_publications.
 */
export interface MessengerCardInput {
  content_type?: "hero" | "video" | "text";
  title: string;
  body?: string | null;
  media?: { url: string; kind: "image" | "video" } | null;
  cta?: { style: "links" | "button"; items: Array<{ text: string; action: { type: string; target: string } }> } | null;
  delivery_mode?: "inbox_only" | "push_and_inbox" | "silent_preload" | "in_app_only";
}

export interface Broadcast {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  segment_id: string | null;
  external_segment_id: string | null;
  /** Федеративный сегмент (Фаза 4): аудитория считается на витрине данных (zero-copy). */
  federated_segment_id: string | null;
  /** Федеративная композиция (вариант «б»): ∪∩∖ над федеративными сегментами (zero-copy). */
  federated_composition_id: string | null;
  // Фаза 4 — null для send-режима «Мессенджер» (задан messenger_card).
  channel_id: string | null;
  template_id: string | null;
  /** Фаза 4 — канал «Мессенджер»: карточка + цель доставки (inbox/feed). */
  messenger_card?: MessengerCardInput | null;
  messenger_target?: "inbox" | "feed" | null;
  messenger_feed_template_id?: string | null;
  /**
   * B16 — when set, this broadcast is a «survey campaign»: the executor mints a
   * personal survey-response token per recipient and exposes it as the Liquid
   * variable {{ survey_link }} (alias {{ survey_url }}) in that recipient's
   * render scope, so each message carries a unique personal answer link.
   * null = a normal broadcast.
   */
  survey_id: string | null;
  cascade_policy_id: string | null;
  sto_enabled: boolean;
  variants?: BroadcastVariant[] | null;
  ab_test_percent?: number | null;
  ab_winner_metric?: "clicks" | "opens";
  ab_test_window_hours?: number;
  ab_test_started_at?: string | null;
  ab_decision_at?: string | null;
  ab_winner_variant?: string | null;
  delivery_window?: DeliveryWindow | null;
  traffic_category: TrafficCategory;
  throttle_per_sec: number | null;
  send_evenly?: boolean;
  /** B2 — лимит бюджета рассылки (копейки). null = без лимита. */
  budget_kopecks?: number | null;
  budget_pause_on_exhaust?: boolean;
  /** B2 — под-состояние бюджета: null|active|warning|paused_budget|stopped_budget. */
  budget_state?: "active" | "warning" | "paused_budget" | "stopped_budget" | null;
  /** B2 — прогресс бюджета (только в GET /:id, когда задан лимит). */
  budget_status?: {
    budget_kopecks: number;
    spent_kopecks: number;
    budget_state: "active" | "warning" | "paused_budget" | "stopped_budget" | null;
    run_id: string;
    recipient_count: number;
    sent_count: number;
    delivered_count: number;
    failed_count: number;
  } | null;
  recurrence_rule: string | null;
  /** Phase-3 единое расписание — структурная модель ScheduleModel (jsonb). null = старое расписание (только recurrence_rule). */
  schedule?: Record<string, unknown> | null;
  scheduled_at: string | null;
  next_run_at: string | null;
  status: BroadcastStatus;
  approval_required?: boolean;
  n_required_approvals?: number;
  stats: Record<string, number>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface BroadcastRun {
  id: string;
  tenant_id: string;
  broadcast_id: string;
  recipient_count: number;
  estimated_cost_kopecks: number;
  actual_cost_kopecks: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  failed_count: number;
  unsubscribed_count: number;
  ack_count: number;
  status: BroadcastRunStatus;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface BroadcastMessage {
  id: string;
  broadcast_id: string;
  run_id: string;
  profile_id: string | null;
  recipient: string;
  channel: ChannelType;
  actual_channel: ChannelType | null;
  broker_id: string | null;
  status: MessageStatus;
  status_at: string | null;
  error_code: string | null;
  error_description: string | null;
  cost_kopecks: number | null;
  created_at: string;
}

export interface CreateBroadcastInput {
  name: string;
  description?: string | null;
  segment_id?: string | null;
  external_segment_id?: string | null;
  /** Федеративный сегмент (Фаза 4). */
  federated_segment_id?: string | null;
  /** Федеративная композиция (вариант «б»). Бэкенд требует РОВНО ОДНО из четырёх audience-ключей. */
  federated_composition_id?: string | null;
  // Фаза 4 — необязательны для канала «Мессенджер» (задан messenger_card).
  channel_id?: string | null;
  template_id?: string | null;
  /** Фаза 4 — канал «Мессенджер»: карточка + цель (inbox/feed) + лента для feed. */
  messenger_card?: MessengerCardInput | null;
  messenger_target?: "inbox" | "feed" | null;
  messenger_feed_template_id?: string | null;
  /**
   * B16 — optional survey to attach to this broadcast (see {@link Broadcast.survey_id}).
   * The create endpoint accepts it; the executor uses it to mint the per-recipient
   * {{ survey_link }} token. Omit / null for a normal broadcast.
   */
  survey_id?: string | null;
  cascade_policy_id?: string | null;
  sto_enabled?: boolean;
  variants?: BroadcastVariant[] | null;
  ab_test_percent?: number | null;
  ab_winner_metric?: "clicks" | "opens";
  ab_test_window_hours?: number;
  delivery_window?: DeliveryWindow | null;
  traffic_category?: TrafficCategory;
  throttle_per_sec?: number | null;
  send_evenly?: boolean;
  /** B2 — лимит бюджета рассылки (копейки). null = без лимита. */
  budget_kopecks?: number | null;
  budget_pause_on_exhaust?: boolean;
  recurrence_rule?: string | null;
  /** Phase-3 единое расписание — структурная модель (jsonb). Когда задано, движок выводит recurrence_rule из неё. */
  schedule?: Record<string, unknown> | null;
  scheduled_at?: string | null;
}

export interface UpdateBroadcastInput {
  name?: string;
  description?: string | null;
  segment_id?: string | null;
  external_segment_id?: string | null;
  /** Федеративный сегмент (Фаза 4). */
  federated_segment_id?: string | null;
  /** Федеративная композиция (вариант «б»). */
  federated_composition_id?: string | null;
  channel_id?: string | null;
  template_id?: string | null;
  /** Фаза 4 — канал «Мессенджер». */
  messenger_card?: MessengerCardInput | null;
  messenger_target?: "inbox" | "feed" | null;
  messenger_feed_template_id?: string | null;
  cascade_policy_id?: string | null;
  sto_enabled?: boolean;
  variants?: BroadcastVariant[] | null;
  ab_test_percent?: number | null;
  ab_winner_metric?: "clicks" | "opens";
  ab_test_window_hours?: number;
  delivery_window?: DeliveryWindow | null;
  traffic_category?: TrafficCategory;
  throttle_per_sec?: number | null;
  send_evenly?: boolean;
  /** B2 — лимит бюджета рассылки (копейки). null = без лимита. */
  budget_kopecks?: number | null;
  budget_pause_on_exhaust?: boolean;
  recurrence_rule?: string | null;
  /** Phase-3 единое расписание — структурная модель (jsonb). Когда задано, движок выводит recurrence_rule из неё. */
  schedule?: Record<string, unknown> | null;
  scheduled_at?: string | null;
  status?: BroadcastStatus;
}

export interface ListParams {
  limit?: number;
  offset?: number;
  status?: BroadcastStatus;
  include_archived?: boolean;
}

/** Фича 4 — строка отчёта «A/B времени отправки» по одному слоту (арме). */
export interface SendTimeABArm {
  variant_id: string;
  send_hour: number;
  label: string;            // "09:00"
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  open_pct: number;
  ctr_pct: number;
  converted: number;
  conv_pct: number;
  revenue_kopecks: number;
  is_control: boolean;
  is_winner: boolean;
  uplift_pct: number | null;
  p_value: number | null;
  significant: boolean;
  /** Пункт «a» — доля остатка на слот (%): в адаптивном режиме = Байесов вес. */
  remainder_share_pct: number;
  /** Пункт «в» — конверсия между соседними ступенями воронки (кумулятивно, ≤100%). */
  step_delivered_pct: number;
  step_opened_pct: number;
  step_clicked_pct: number;
  /** Пункт «в» — распределение времени реакции слота. */
  reaction: SendTimeReactionDist;
}

export interface SendTimeReactionBucket {
  label: string;
  opens: number;
  clicks: number;
}
export interface SendTimeReactionDist {
  median_open_min: number | null;
  median_click_min: number | null;
  open_samples: number;
  click_samples: number;
  buckets: SendTimeReactionBucket[];
}

export interface SendTimeABReport {
  broadcast_id: string;
  broadcast_name: string;
  channel: string;
  primary_metric: "clicks" | "opens";
  attribution_window_days: number;
  winner_variant_id: string | null;
  winner_significant: boolean;
  /** Пункт «a» — адаптивный режим (Thompson) включён. */
  adaptive: boolean;
  /** Пункт «a» — адаптивное решение принято (остаток перевзвешен). */
  decided: boolean;
  arms: SendTimeABArm[];
}

export const broadcastsApi = {
  async list(params: ListParams = {}): Promise<{ rows: Broadcast[]; total: number }> {
    const { data } = await apiClient.get("/v1/broadcasts", { params });
    return data;
  },
  async get(id: string): Promise<Broadcast> {
    const { data } = await apiClient.get(`/v1/broadcasts/${id}`);
    return data.broadcast;
  },
  async create(input: CreateBroadcastInput): Promise<Broadcast> {
    const { data } = await apiClient.post("/v1/broadcasts", input);
    return data.broadcast;
  },
  async update(id: string, input: UpdateBroadcastInput): Promise<Broadcast> {
    const { data } = await apiClient.patch(`/v1/broadcasts/${id}`, input);
    return data.broadcast;
  },
  async archive(id: string): Promise<void> {
    await apiClient.delete(`/v1/broadcasts/${id}`);
  },
  async start(id: string): Promise<BroadcastRun> {
    const { data } = await apiClient.post(`/v1/broadcasts/${id}/start`);
    return data.run;
  },
  /** B2 — «Добавить бюджет и продолжить»: поднять лимит и продолжить рассылку,
   *  приостановленную по бюджету (идемпотентно — уже отправленным не шлём). */
  async resume(id: string, budgetKopecks: number): Promise<{ run_id: string; budget_kopecks: number; spent_kopecks: number }> {
    const { data } = await apiClient.post(`/v1/broadcasts/${id}/resume`, { budget_kopecks: budgetKopecks });
    return data;
  },
  async schedule(id: string, scheduledAt: string): Promise<Broadcast> {
    const { data } = await apiClient.post(`/v1/broadcasts/${id}/schedule`, {
      scheduled_at: scheduledAt,
    });
    return data.broadcast;
  },
  async cancel(id: string): Promise<Broadcast> {
    const { data } = await apiClient.post(`/v1/broadcasts/${id}/cancel`);
    return data.broadcast;
  },
  async runs(
    id: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<{ rows: BroadcastRun[]; total: number }> {
    const { data } = await apiClient.get(`/v1/broadcasts/${id}/runs`, { params });
    return data;
  },
  async stats(id: string): Promise<{
    broadcast_id: string;
    cached: Record<string, number>;
    live: Record<string, number>;
  }> {
    const { data } = await apiClient.get(`/v1/broadcasts/${id}/stats`);
    return data;
  },
  /** Фича 4 — отчёт «A/B времени отправки»: воронка по слотам + победивший час.
   *  null → рассылка не является экспериментом времени (нет ≥2 арм с send_hour). */
  async sendTimeReport(id: string): Promise<SendTimeABReport | null> {
    try {
      const { data } = await apiClient.get(`/v1/broadcasts/${id}/send-time-report`);
      return data;
    } catch (e) {
      if ((e as { response?: { status?: number } }).response?.status === 404) return null;
      throw e;
    }
  },
  async getRun(runId: string): Promise<BroadcastRun> {
    const { data } = await apiClient.get(`/v1/broadcast-runs/${runId}`);
    return data.run;
  },
  async runMessages(
    runId: string,
    params: { limit?: number; offset?: number; status?: MessageStatus } = {},
  ): Promise<{ rows: BroadcastMessage[]; total: number }> {
    const { data } = await apiClient.get(`/v1/broadcast-runs/${runId}/messages`, { params });
    return data;
  },

  /**
   * Pre-create cost estimate. UI inline in the Create modal + Approvals.
   */
  async estimateCost(input: {
    segment_id?: string | null;
    external_segment_id?: string | null;
    federated_segment_id?: string | null;
    federated_composition_id?: string | null;
    channel_id: string;
    template_id: string;
    traffic_category?: TrafficCategory;
    sample_size?: number;
  }): Promise<CostEstimateOutput> {
    const { data } = await apiClient.post("/v1/broadcasts/estimate-cost", input);
    return data;
  },

  /**
   * Predicted + actual + variance for an existing broadcast. UI: KPI tile.
   */
  async costSummary(id: string): Promise<CostSummaryOutput> {
    const { data } = await apiClient.get(`/v1/broadcasts/${id}/cost-summary`);
    return data;
  },

  /**
   * Pre-send suppression preview (B5): how many of the segment would be
   * suppressed (consent / caps / quiet hours) vs delivered, for this channel.
   */
  async suppressionPreview(input: {
    segment_id: string;
    channel_id: string;
    traffic_category?: TrafficCategory;
  }): Promise<SuppressionPreviewOutput> {
    const { data } = await apiClient.post("/v1/broadcasts/suppression-preview", input);
    return data;
  },
};

export interface SuppressionPreviewOutput {
  total: number;
  evaluated: number;
  sampled: boolean;
  deliverable: number;
  suppressed: {
    total: number;
    by_reason: { consent: number; caps: number; quiet_hours: number; other: number };
  };
}

export interface CostEstimateOutput {
  recipients_count: number;
  sample_size: number;
  avg_cost_kopecks: number;
  total_cost_kopecks: number;
  channel_type: string;
  breakdown_by_carrier: Record<string, number>;
  warnings: string[];
}

export interface CostSummaryOutput {
  broadcast_id: string;
  predicted_kopecks: number;
  predicted_from_runs_kopecks: number;
  actual_kopecks: number;
  variance_pct: number | null;
  runs_count: number;
  breakdown_by_carrier: Record<string, number>;
  warnings: string[];
}
